import React, { useState, useEffect, useRef } from 'react';
import { Layout, Drawer } from 'antd';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  DashboardOutlined, SettingOutlined, MessageOutlined,
  SettingOutlined as AdminPanelIcon, LogoutOutlined, HistoryOutlined
} from '@ant-design/icons';
import SiderItem from './SiderItem';
import './Sider.css';

const Sider = ({ user, investmentType, onSelectInvestmentType, availableTypes, onLogout }) => {
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mobileNavRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLogoClick = () => {
    if (user) {
      navigate('/');
    } else {
      navigate('/login');
    }
  };

  useEffect(() => {
    if (!isMobile) {
      document.documentElement.style.removeProperty('--mobile-nav-height');
      return;
    }

    const updateMobileNavHeight = () => {
      if (mobileNavRef.current) {
        const navHeight = mobileNavRef.current.offsetHeight;
        document.documentElement.style.setProperty('--mobile-nav-height', `${navHeight}px`);
      }
    };

    updateMobileNavHeight();
    window.addEventListener('resize', updateMobileNavHeight);

    return () => {
      window.removeEventListener('resize', updateMobileNavHeight);
      document.documentElement.style.removeProperty('--mobile-nav-height');
    };
  }, [isMobile, user, availableTypes, investmentType]);

  // 투자 유형 선택 시 리다이렉트 처리 (Header와 동일한 로직)
  const handleInvestmentTypeChange = (value) => {
    if (typeof onSelectInvestmentType === 'function') {
      onSelectInvestmentType(value);
    }
    const currentPath = location.pathname;
    const currentSearchParams = new URLSearchParams(searchParams);
    
    // 현재 URL과 동일한지 확인하여 무한 루프 방지
    const newDataType = value === 'crypto' ? 'legacy' : value;
    if (currentPath === '/fixed-income-flow-history' && newDataType !== 'fixed-income') {
      navigate(`/dashboard?dataType=${newDataType}`);
      return;
    }
    const newUrl = `${currentPath}?dataType=${newDataType}`;
    const currentUrl = `${currentPath}?${currentSearchParams.toString()}`;
    
    if (newUrl !== currentUrl) {
      navigate(newUrl);
    }
  };

  // URL 파라미터에 따라 현재 선택된 투자 유형 결정 (Header와 동일한 로직)
  const getCurrentInvestmentType = () => {
    if (location.pathname === '/fixed-income-flow-history') return 'fixed-income';
    const urlDataType = searchParams.get('dataType');
    if (urlDataType === 'legacy') return 'crypto';
    if (urlDataType === 'preferred-return') return 'preferred-return';
    if (urlDataType === 'fixed-income') return 'fixed-income';
    return investmentType || 'crypto';
  };

  const getRoutes = () => {
    if (!user) {
      return []; // 비로그인 사용자는 메뉴 없음
    }

    const currentType = getCurrentInvestmentType();
    const currentDataType = currentType === 'crypto' ? 'legacy' : currentType;
    const isFixedIncomeContext =
      location.pathname === '/fixed-income-flow-history' ||
      searchParams.get('dataType') === 'fixed-income' ||
      currentType === 'fixed-income' ||
      investmentType === 'fixed-income';
    const withDataType = (basePath) => {
      if (user.type === 'admin') return basePath;
      return `${basePath}?dataType=${currentDataType}`;
    };

    const baseRoutes = [
      { basePath: '/dashboard', path: withDataType('/dashboard'), label: 'Dashboard', icon: <DashboardOutlined /> },
    ];
    
    if (user.type !== 'admin') {
      if ((availableTypes || []).includes('fixed-income') && isFixedIncomeContext) {
        baseRoutes.push(
          { basePath: '/fixed-income-flow-history', path: withDataType('/fixed-income-flow-history'), label: 'History', icon: <HistoryOutlined /> }
        );
      }
      baseRoutes.push(
        { basePath: '/account-settings', path: withDataType('/account-settings'), label: 'Account Settings', icon: <SettingOutlined /> }
      );
      baseRoutes.push(
        { basePath: '/contact', path: withDataType('/contact'), label: 'Contact Us', icon: <MessageOutlined /> }
      );
    }
    
    if (user.type === 'admin') {
      baseRoutes.push(
        { basePath: '/admin', path: '/admin', label: 'Admin Panel', icon: <AdminPanelIcon /> }
      );
    }
    
    return baseRoutes;
  };

  const routes = getRoutes();

  // 모바일 상단 네비게이션
  if (isMobile) {
    return (
      <div className="mobile-navigation" ref={mobileNavRef}>
        <div className="mobile-nav-header">
          <img 
            src="/logo-black.png" 
            alt="VegaX" 
            className="mobile-logo" 
            onClick={handleLogoClick}
          />
        </div>
        {user && (
          <>
            {/* Investment Type 선택기 */}
            {availableTypes && availableTypes.length > 0 && (
              <div className="mobile-investment-type-section">
                <select
                  className="mobile-investment-type-select"
                  value={getCurrentInvestmentType()}
                  onChange={e => handleInvestmentTypeChange(e.target.value)}
                >
                  {availableTypes.includes('legacy') && (
                    <option value="crypto">Digital Assets</option>
                  )}
                  {availableTypes.includes('preferred-return') && 
                  availableTypes.includes('fixed-income') && (
                    <option value="preferred-return">Preferred Return</option>
                  )}
                  {availableTypes.includes('preferred-return') && 
                  availableTypes.includes('fixed-income') && (
                    <option value="fixed-income">Fixed Income</option>
                  )}
                  {availableTypes.includes('preferred-return') && 
                  !availableTypes.includes('fixed-income') && (
                    <option value="preferred-return">Preferred Return</option>
                  )}
                  {availableTypes.includes('fixed-income') && 
                  !availableTypes.includes('preferred-return') && (
                    <option value="fixed-income">Fixed Income</option>
                  )}
                </select>
              </div>
            )}
            <div className="mobile-nav-tabs">
              {routes.map((route) => (
                <div
                  key={route.path}
                  className={`mobile-nav-tab ${location.pathname === (route.basePath || route.path) ? 'active' : ''}`}
                  onClick={() => navigate(route.path)}
                >
                  <span className="mobile-nav-icon">{route.icon}</span>
                  <span className="mobile-nav-label">{route.label}</span>
                </div>
              ))}
            </div>
            <div className="mobile-user-actions">
              <div className="mobile-user-info">
                <div className="mobile-user-avatar-badge">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="mobile-user-meta">
                  <span className="mobile-user-name">{user?.name || 'User'}</span>
                  {user?.email && (
                    <span className="mobile-user-email">{user.email}</span>
                  )}
                </div>
              </div>
              <button 
                className="mobile-logout-button"
                onClick={onLogout}
                type="button"
              >
                <LogoutOutlined />
                <span>Logout</span>
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // 데스크톱 Sider
  const content = (
    <div className="sider-content">
      <header>
        <img 
          src="/logo-black.png" 
          alt="VegaX" 
          className="sider-logo" 
          onClick={handleLogoClick}
          style={{ cursor: 'pointer' }}
        />
      </header>
      {user && (
        <nav>
          {routes.map((route) => (
            <SiderItem 
              key={route.path}
              item={route} 
              isActive={location.pathname === (route.basePath || route.path)}
            />
          ))}
        </nav>
      )}
    </div>
  );

  return (
    <Layout.Sider 
      width={243} 
      collapsible={false}
      style={{ zIndex: 2 }}
      className="transparent-sider"
    >
      {content}
    </Layout.Sider>
  );
};

export default Sider;
